import React, { useMemo, useCallback, useState, ChangeEvent } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { debounce } from 'utils/debounce'
import { useFetchProducts } from 'features/ECommerce/api/queries/useFetchProducts'
import { ProductTypes } from 'models/Product'
import { Proteins } from 'models/Pet'
import { Layout } from 'components/Layout'
import { Footer } from 'components/Footer'
import { Text } from 'components/Text'
import { VerticalSeparator } from 'components/VerticalSeparator'
import { AllProductsHeader } from './components/AllProductsHeader'
import { ProductTypeSection } from './components/ProductTypeSection'
import {
  AllProductsRoot,
  Wrapper,
  SectionHeader,
  SectionBody,
  Results,
  EmptyStateWrapper,
} from './AllProducts.styles'
import { checkboxToggler } from 'features/ECommerce/utils/checkbox-toggler'
import { EmptyStateProduct } from 'features/EmptyStates/components/EmptyStateProduct'
import { Loader } from 'features/components/Loader'
import { fbConversion } from '../../../../api/analytics/analytics'

type AllProductsProps = {}

export const AllProducts = (_props: AllProductsProps) => {
  fbConversion('ViewProducts')
  const { t } = useTranslation()

  const [typesFilter, setTypesFilter] = useState<ProductTypes[]>([])
  const [proteinsFilter, setProteinsFilter] = useState<Proteins[]>([])
  const [query, setQuery] = useState('')

  const { data: results, status } = useFetchProducts(
    {
      page: 1,
      limit: 10,
      proteins: proteinsFilter,
      types: typesFilter,
      textSearch: query,
    },
    { keepPreviousData: true }
  )

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    debounce(setQuery, 400)(e.target.value)
  }

  const onTypesChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      setTypesFilter(
        checkboxToggler<ProductTypes>(
          typesFilter,
          e.target.name as ProductTypes | 'all',
          Object.values(ProductTypes)
        )
      ),
    [typesFilter]
  )

  const onProteinsChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      setProteinsFilter(
        checkboxToggler<Proteins>(
          proteinsFilter,
          e.target.name as Proteins | 'all',
          Object.values(Proteins)
        )
      ),
    [proteinsFilter]
  )

  const header = useMemo(
    () => (
      <AllProductsHeader
        onTypesChange={onTypesChange}
        onProteinsChange={onProteinsChange}
        handleSearch={handleSearch}
        typesFilter={typesFilter}
        proteinsFilter={proteinsFilter}
      />
    ),
    [onProteinsChange, onTypesChange, proteinsFilter, typesFilter]
  )

  if (status === 'loading') return <Loader />

  const patties = results?.products.filter((product) => product.type === ProductTypes.PATTIES)
  const bones = results?.products.filter((product) => product.type === ProductTypes.BONES)
  const treats = results?.products.filter((product) => product.type === ProductTypes.TREATS)


  return (
    <AllProductsRoot>
      <Layout header={header} hideCTA>
        <Wrapper>
          <SectionHeader gap="5">
            <Text size="h3" as="h3" weight="bold" color="primary-800">
              {t('e-commerce:section-title')}
            </Text>
            <VerticalSeparator />
            <Text size="headline" color="primary-800">
              {t('e-commerce:section-description')}
            </Text>
          </SectionHeader>

          <SectionBody>
            {/* <MenuWrapper direction="column" gap="3">
              <SearchWrapper>
                <TextInput
                  label={t('e-commerce:search')}
                  placeholder={t('e-commerce:search')}
                  suffix={<Icon name="search" color="primary-800" />}
                  onChange={handleSearch}
                  short
                />
              </SearchWrapper>
              <ProductTypesMenu typesFilter={typesFilter} onTypesChange={onTypesChange} />
              <ProteinsMenu proteinsFilter={proteinsFilter} onProteinsChange={onProteinsChange} />
            </MenuWrapper> */}

            <Results>
              {!results?.products.length && (
                <EmptyStateWrapper>
                  <EmptyStateProduct />
                </EmptyStateWrapper>
              )}
              <ProductTypeSection type={ProductTypes.PATTIES} products={patties} />
              <ProductTypeSection type={ProductTypes.BONES} products={bones} />
              <ProductTypeSection type={ProductTypes.TREATS} products={treats} />
            </Results>
          </SectionBody>
        </Wrapper>
        <Footer />
      </Layout>
    </AllProductsRoot>
  )
}

AllProducts.displayName = 'AllProducts'
